import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import { toIsoUtc } from "../chiefComplaints/dateHelpers";
import LabInvestigationService from "./LabInvestigationService";
import LabTestCatalogAutocomplete from "./LabTestCatalogAutocomplete";
import { flagBand, nameWithTag } from "./data";
import { LI_V2_REFRESH } from "./events";
import { CalendarIcon, ChevronUpIcon } from "./labIcons";

/**
 * ONE lab-investigation sheet — add or update — owning all of its own field
 * state (Figma 3041:11589, "Modal · Add result").
 *
 * This used to be the body of LabInvestigationEditor, with test / testDate /
 * value / passthrough hoisted into that component and gated on
 * `formOpen` + `editing` + a `submittingRef`. It was split out so the rail can
 * hold a STACK of sheets: each card needs its own picked test, date, result
 * and carried-through labName/description, and sibling cards must not see each
 * other's values. The rail (LabInvestigationEditor) now only decides which
 * cards exist and in what order; everything below the card border lives here.
 *
 * Composition, top to bottom (unchanged from before the split):
 *   Investigation (fuzzy search) / Panel · Auto / Test date /
 *   Result (unit inline) | Reference range · Auto / Value reads as /
 *   Cancel · Save
 *
 * Three deliberate divergences from the board, each because the board assumes
 * a backend that does not exist or drops a function it should not:
 *  - "Reference range · Auto" is READ-ONLY and rides in with the picked test or
 *    the stored record — no catalogue endpoint serves one for fresh picks, so
 *    an untyped "—" is the honest display.
 *  - There is NO delete affordance. The board draws none, the table lost its
 *    delete column to the 6-column layout, and the editor's "Delete this
 *    entry" button was removed on request. `remove()` below is deliberately
 *    kept — the service call and its version handling are still correct, so
 *    re-attaching a control (row trash or editor button) is a one-liner —
 *    but nothing currently calls it, so a lab row cannot be deleted from the
 *    UI at all.
 *  - `labName`/`description` have no fields (the board dropped them) but are
 *    PASSED THROUGH unchanged on edit, so opening and re-saving an old record
 *    cannot silently wipe them.
 *
 * Version policy:
 *   • Add — sends -1 (server-managed retry loop). Appends are drift-safe
 *     because the outcome (a new Investigation, or a StatusEntry appended
 *     via testIdentityKey dedup) doesn't depend on the pre-state.
 *   • Update / Delete — refetch the current version via svc.list()
 *     immediately before the write; strict OCC prevents clobbering a
 *     concurrent edit of the same row. Sibling sheets are blocked from
 *     saving while ours is in flight, so the version cannot go stale
 *     between the refetch and the write.
 */

const toDateInput = (iso) => (iso ? iso.slice(0, 10) : "");
const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyTest = { sltCode: "", ltCode: "", name: "", panel: "", unit: "", refRange: {}, tag: "" };

/** Mirror of the server's ComputeFlag, for the live "Value reads as" preview. */
const previewFlag = (numeric, low, high) => {
  if (numeric == null || isNaN(numeric)) return "unknown";
  if (low == null && high == null) return "unknown";
  if (low != null && numeric < low) return "low";
  if (high != null && numeric > high) return "high";
  return "normal";
};

const parseNumeric = (v) => {
  const n = parseFloat(String(v).trim());
  return isFinite(n) ? n : null;
};

/* Exported for the mobile lab cards' "Ref …" line, so a range reads the same
   there as in this form's "Reference range · Auto" field. */
export const rangeToText = (r) => {
  if (!r) return "";
  if (r.low != null && r.high != null) return `${r.low}–${r.high}`;
  if (r.low != null) return `≥ ${r.low}`;
  if (r.high != null) return `≤ ${r.high}`;
  return r.text || "";
};

// The record's own test snapshot, in exactly the shape the form's `test` state
// carries. Mirrors what the old LI_V2_EDIT listener seeded, field for field —
// plus the reading's tag: an update REPLACES the stored snapshot wholesale, so
// a tag the form did not carry would be wiped from the record.
const testFrom = (editing) => {
  const t = (editing && editing.test) || emptyTest;
  return {
    sltCode: t.sltCode || "",
    ltCode: t.ltCode || "",
    loincCode: t.loincCode || "",
    name: t.name || "",
    panel: t.panel || "",
    unit: t.unit || "",
    refRange: t.refRange || {},
    tag: t.tag || "",
  };
};

const LabInvestigationForm = ({
  formId,
  patientId,
  editing,
  ordinal,
  showOrdinal,
  blockedBySibling,
  onFilledChange,
  onSavingChange,
  onClose,
  // appointmentId is the currently-selected appointment on the patient
  // detail screen; the standalone Add / Update stamps it onto the LI
  // v2 write body so the reading is scoped to that appointment.
  // Transcript mode carries it through onto the captured payload so the
  // Go SaveSection fan-out forwards the same id to LI v2 on Save & next.
  appointmentId,
  // Transcript-mode extensions (all optional; defaults preserve standalone).
  initialPayload,
  onSave,
  saving,
  error,
  themeClass,
  lockName,
}) => {
  const isUpdate = !!editing;
  const p = initialPayload || null;
  const isTranscript = typeof onSave === "function";
  const lockTestName = !!lockName;

  // Seeded in the useState INITIALISER, not an effect: a sheet mounts once per
  // record and never switches the record underneath itself.
  const [test, setTest] = useState(() => {
    if (isUpdate) return testFrom(editing);
    if (p) {
      return {
        sltCode:   "",
        ltCode:    "",
        loincCode: p.loinc_code || "",
        name:      p.test_name || "",
        panel:     p.investigation_panel || "",
        unit:      p.result_units || "",
        refRange:  p.ref_range ? { text: p.ref_range } : {},
      };
    }
    return emptyTest;
  });
  const [collapsed, setCollapsed] = useState(false);
  const [testDate, setTestDate] = useState(() => {
    if (isUpdate) return toDateInput(editing.testDate) || todayISO();
    if (p && p.test_date) return String(p.test_date).slice(0, 10);
    return todayISO();
  });
  const [value, setValue] = useState(() => {
    if (isUpdate) return editing.value || "";
    if (p) return p.result_value || "";
    return "";
  });
  // labName carried invisibly so re-saving an old record keeps it (see header).
  const [passthrough] = useState(() => {
    if (isUpdate) return { labName: editing.labName || "" };
    return { labName: "" };
  });
  // Description is a user-editable field (ZY-10340) — surfaced as an
  // optional textarea below "Value reads as". Seeded from the record
  // on update / from the transcript payload on capture-fill.
  const [description, setDescription] = useState(() => {
    if (isUpdate) return editing.description || "";
    if (p) return p.description || "";
    return "";
  });
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // Stops setState after the rail drops this sheet (or the section switches)
  // mid-flight.
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  // The rail needs to know whether this sheet has an investigation on it yet:
  // a second "Add manually" click must not bury an untouched blank sheet under
  // a new one. Update sheets always count as filled — their test comes from the
  // record.
  useEffect(() => {
    if (typeof onFilledChange === "function") {
      onFilledChange(formId, isUpdate || !!test.name.trim());
    }
  }, [formId, isUpdate, test.name, onFilledChange]);

  const handleTestPick = useCallback((picked) => {
    setTest((prev) => {
      if (picked.sltCode) {
        // The catalogue serves refRange:{} on every row, so taking the pick's
        // range verbatim silently wipes a stored one. Landing back on the
        // RECORD's own test restores the record's range (searching requires
        // typing, which abandons the transient state first — so compare with
        // `editing`, not `prev`); staying on the current pick keeps it; a
        // genuinely different test invalidates it by definition.
        const storedTest = editing && editing.test;
        const backToStored = !!storedTest && storedTest.sltCode === picked.sltCode;
        const samePick = picked.sltCode === prev.sltCode;
        return {
          sltCode: picked.sltCode,
          ltCode: picked.ltCode,
          loincCode: picked.loincCode || "",
          name: picked.name,
          panel: picked.panel || "",
          unit: picked.unit || "",
          refRange: backToStored
            ? storedTest.refRange || {}
            : samePick
            ? prev.refRange
            : picked.refRange || {},
          // Same rule for the tag: the record's own test keeps the record's
          // tag; a catalogue pick of another test has none.
          tag: backToStored ? storedTest.tag || "" : samePick ? prev.tag || "" : "",
        };
      }
      // Free-text keystrokes. While the text still reads as the picked name,
      // only the code link is dropped; the moment it diverges the pick is
      // abandoned WHOLESALE — otherwise the body ships the abandoned pick's
      // panel/unit/range under a name that means something else.
      const sameName =
        (picked.name || "").trim().toLowerCase() === (prev.name || "").trim().toLowerCase();
      if (sameName) return { ...prev, sltCode: "", ltCode: "" };
      return { ...emptyTest, name: picked.name };
    });
  }, [editing]);

  const numeric = useMemo(() => parseNumeric(value), [value]);
  // The flag reads the refRange OBJECT (stored records carry {low, high});
  // descriptive text ranges honestly yield "unknown", never guessed bounds.
  const flag = useMemo(
    () => previewFlag(numeric, test.refRange.low, test.refRange.high),
    [numeric, test.refRange.low, test.refRange.high]
  );
  const band = flagBand(flag);

  const isBusy = submitting || !!saving;
  // Per ZY-10340, either value OR description must be provided (backend
  // enforces the same rule via ValidateValueOrDescription). Test name +
  // testDate stay strictly required.
  const canSubmit =
    !blockedBySibling && !isBusy && !!test.name.trim() && !!testDate
    && (!!value.trim() || !!description.trim());

  const submit = () => {
    if (!canSubmit) return;
    if (!isTranscript && !patientId) return;

    // appointmentId is stamped onto every Add / Update body when the
    // parent (PatientShell → LabInvestigationEditor → this form) had
    // one in scope. LI v2 expects int64; a missing / zero appointment
    // is dropped from the wire by the server's omitempty.
    const aptId = Number(appointmentId);
    const body = {
      test: {
        sltCode: test.sltCode,
        ltCode: test.ltCode,
        loincCode: test.loincCode || "",
        name: test.name.trim(),
        panel: test.panel.trim(),
        unit: test.unit.trim(),
        // Verbatim round-trip: stored {low, high} or {text} survives an edit
        // untouched; fresh picks carry {} (no service stores ranges yet).
        refRange: test.refRange || {},
        // Only a stored reading has one; catalogue picks send no key at all.
        ...(test.tag ? { tag: test.tag } : {}),
      },
      testDate: toIsoUtc(testDate),
      value: value.trim(),
      numericValue: numeric != null ? numeric : undefined,
      labName: passthrough.labName,
      source: editing ? editing.source || "manual" : "manual",
      description: description.trim(),
      appointmentId: Number.isFinite(aptId) && aptId > 0 ? aptId : 0,
    };

    // Transcript mode — hand out; caller owns persistence. Skip local
    // submitting/feedback + LI_V2_REFRESH emit.
    if (isTranscript) {
      onSave(body);
      return;
    }

    setSubmitting(true);
    if (typeof onSavingChange === "function") {
      onSavingChange(formId, true);
    }
    setFeedback(null);

    const svc = new LabInvestigationService();
    const done = () => {
      // The write committed either way; only the local state needs a liveness
      // check (section switches don't wait for round trips).
      EventService.emit(LI_V2_REFRESH);
      if (!mounted.current) return;
      if (typeof onSavingChange === "function") {
        onSavingChange(formId, false);
      }
      // The rail drops this card, which unmounts us — so nothing is reset or
      // re-rendered here. Anything set after this point would be a setState on
      // an unmounted component.
      onClose(formId);
    };
    const failed = (err) => {
      // Unmounted means the rail already dropped this sheet, and closeSheet
      // clears the saving flag on the way out — so there is nothing to hand
      // back and no parent state to touch.
      if (!mounted.current) return;
      if (typeof onSavingChange === "function") {
        onSavingChange(formId, false);
      }
      setSubmitting(false);
      setFeedback({ kind: "err", text: (err && err.message) || "Save failed — please retry" });
    };
    // Version policy per op:
    //   • Add — append-only. Server dedups by testIdentityKey and either
    //     appends a fresh Investigation or a StatusEntry, so the outcome
    //     doesn't depend on doc pre-state. Send as a length-1 batch with
    //     expectedVersion=-1 so the backend runs its server-managed 3-
    //     attempt retry loop (LI v2's single-Add endpoint is retired per
    //     ADR-0001 Phase 2 — Batch is the append path).
    //   • Update — mutates a specific Investigation's top-level fields.
    //     Refetch the current version immediately before the write so
    //     we're not overwriting a concurrent edit.
    if (!editing) {
      svc.batch(
        patientId,
        {
          source: body.source,
          expectedVersion: -1,
          investigations: [body],
        },
        done,
        failed
      );
      return;
    }
    svc.list(
      patientId,
      (res) => {
        const v = (res && res.version) || 0;
        svc.update(patientId, editing.investigationId, { ...body, expectedVersion: v }, done, failed);
      },
      failed
    );
  };

  // Retained but unreferenced — see the file header. Re-attach a control to
  // restore delete; the wire call and version handling below are intact. The
  // onSavingChange bracket matches submit()'s: a delete holds the same
  // stack-wide save lock, so a re-attached control cannot strand the siblings.
  // eslint-disable-next-line no-unused-vars
  const remove = () => {
    // blockedBySibling too: a DELETE firing inside a sibling's
    // list->write window is exactly the concurrent write the stack-wide
    // lock exists to prevent.
    if (!editing || submitting || blockedBySibling) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove ${test.name} from lab investigations?`)) return;
    setSubmitting(true);
    onSavingChange(formId, true);
    const svc = new LabInvestigationService();
    svc.list(
      patientId,
      (res) => {
        svc.delete(
          patientId,
          editing.investigationId,
          (res && res.version) || 0,
          () => {
            EventService.emit(LI_V2_REFRESH);
            if (!mounted.current) return;
            onSavingChange(formId, false);
            onClose(formId);
          },
          (err) => {
            if (!mounted.current) return;
            onSavingChange(formId, false);
            setSubmitting(false);
            setFeedback({ kind: "err", text: (err && err.message) || "Delete failed — please retry" });
          }
        );
      },
      (err) => {
        if (!mounted.current) return;
        onSavingChange(formId, false);
        setSubmitting(false);
        setFeedback({ kind: "err", text: (err && err.message) || "Delete failed — please retry" });
      }
    );
  };

  // Unique per sheet — stacked cards would otherwise share DOM ids and every
  // label would point at the first card's field.
  const panelId = `li3-panel-${formId}`;
  const dateId = `li3-date-${formId}`;
  const valueId = `li3-value-${formId}`;
  const rangeId = `li3-range-${formId}`;

  const foldId = `li3-fold-${formId}`;

  const rootClass = [
    "li3-form",
    collapsed ? "is-collapsed" : "",
    isTranscript ? "cc3-form" : "",
    themeClass || "",
  ].filter(Boolean).join(" ");
  const displayError = feedback && feedback.kind === "err"
    ? feedback.text
    : error || "";
  const displayErrorKind = displayError ? "err" : "";

  return (
    <div className={rootClass}>
      {/* The chevron-up folds everything below this row, footer included. */}
      <div className="li3-lbl-row">
        {/* The badge rides INSIDE the label: .li3-lbl-row is a space-between
            flex row, so a bare sibling span would be flung to the chevron's
            edge instead of sitting against the label text. */}
        <label className="li3-lbl">
          {showOrdinal && <span className="zy-ord" aria-hidden="true">{ordinal}</span>}
          Investigation{" "}
          {lockTestName ? (
            <span className="cc3-update-name">&middot; {nameWithTag(test, "(none)")}</span>
          ) : (
            <span className="li3-lbl-hint">&middot; fuzzy search</span>
          )}
        </label>
        <button
          type="button"
          className="li3-lbl-chev"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          aria-controls={foldId}
          aria-label={
            collapsed ? "Expand this investigation" : "Collapse this investigation"
          }
        >
          <ChevronUpIcon />
        </button>
      </div>

      <div className="li3-fold is-tight" id={foldId}>
        <div className="li3-fold-inner">
      {!lockTestName && (
        <div className="li3-field">
          <LabTestCatalogAutocomplete
            value={test.name}
            sltCode={test.sltCode}
            onChange={handleTestPick}
            disabled={isBusy}
          />
        </div>
      )}

      <div className="li3-field">
        <label className="li3-lbl" htmlFor={panelId}>Panel &middot; Auto</label>
        {/* Readonly, per the board: the panel IS the picked test's parent —
            typing one that disagrees with the sltCode would corrupt the
            grouping. Free-text tests simply have no panel. */}
        <input
          id={panelId}
          className="li3-input is-auto"
          type="text"
          value={test.panel}
          placeholder="—"
          readOnly
          tabIndex={-1}
        />
      </div>

      <div className="li3-field">
        <label className="li3-lbl" htmlFor={dateId}>Test date</label>
        <div className="li3-input-wrap">
          {/* Test date bounds: 1900-01-01 → today. Same policy as
              DigitiseManuallyCard — pre-1900 breaks the trend chart
              and is almost always a typo. */}
          <input
            id={dateId}
            className="li3-input"
            type="date"
            value={testDate}
            onChange={(e) => {
              const v = e.target.value;
              let clamped = v;
              if (v && v > todayISO()) clamped = todayISO();
              else if (v && v < "1900-01-01") clamped = "1900-01-01";
              setTestDate(clamped);
            }}
            min="1900-01-01"
            max={todayISO()}
            disabled={isBusy}
          />
          <span className="li3-input-icon"><CalendarIcon /></span>
        </div>
      </div>

      <div className="li3-two">
        <div className="li3-field">
          <label className="li3-lbl" htmlFor={valueId}>Result</label>
          {/* Flex, not absolute: a long unit ("Descriptive or %") must
              shrink the input, never overlap it. The wrapper wears the
              border so the pair still reads as one field. */}
          <div className="li3-unit-field">
            <input
              id={valueId}
              type="text"
              value={value}
              maxLength={100}
              onChange={(e) => setValue((e.target.value || "").slice(0, 100))}
              placeholder="0.0"
              disabled={isBusy}
            />
            {test.unit ? <span className="li3-unit" title={test.unit}>{test.unit}</span> : null}
          </div>
        </div>
        <div className="li3-field">
          <label className="li3-lbl" htmlFor={rangeId}>Reference range &middot; Auto</label>
          {/* Read-only (3041:12066): the range rides in with the picked test
              or the stored record — no catalogue endpoint serves one for
              fresh picks, so an untyped "—" is the honest display, and the
              board shows "13.0–17.0 g/dL" (range + unit) when known. */}
          <input
            id={rangeId}
            className="li3-input is-auto"
            type="text"
            value={(() => {
              const t = rangeToText(test.refRange);
              return t ? `${t}${test.unit ? ` ${test.unit}` : ""}` : "";
            })()}
            placeholder="—"
            readOnly
            tabIndex={-1}
          />
        </div>
      </div>

      <div className="li3-field">
        <label className="li3-lbl">Value reads as</label>
        <div className="li3-input is-auto li3-flag">
          {flag === "unknown" ? (
            <span className="li3-flag-none">&mdash;</span>
          ) : (
            <>
              <span className="li3-flag-dot" style={{ background: band.dot }} aria-hidden="true" />
              <span className="li3-flag-word">{band.label}</span>
            </>
          )}
        </div>
      </div>

      {/* Description (ZY-10340). Optional per-entry note. The Save gate
          (canSubmit above) requires EITHER Value OR Description to be
          non-empty; the backend enforces the same rule via
          ValidateValueOrDescription. No length cap. */}
      <div className="li3-field">
        <label className="li3-lbl" htmlFor={`${formId}-desc`}>
          Description <span className="li3-lbl-hint">&middot; optional</span>
        </label>
        <textarea
          id={`${formId}-desc`}
          className="li3-input li3-textarea"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Add description…"
          rows={3}
          disabled={isBusy}
        />
      </div>

      {displayError && <div className={`cc-feedback is-${displayErrorKind}`}>{displayError}</div>}

      <div className="li3-foot">
        {/* Stays live while saving ON PURPOSE. A write that never settles
            (the token-refresh path can swallow its callbacks) would otherwise
            pin the stack-wide save lock with no way out — every sheet's Save
            dead and this sheet undismissable until a reload. Closing mid-write
            is safe: the rail clears the lock, and both done() and failed()
            bail on !mounted, so a late response touches nothing. */}
        <button
          type="button"
          className="li3-cancel"
          onClick={() => onClose(formId)}
        >
          {isBusy ? "Close" : "Cancel"}
        </button>
        <button
          type="button"
          className={`li3-save${themeClass ? ` cc3-save ${themeClass}` : ""}`}
          onClick={submit}
          disabled={!canSubmit}
          title={blockedBySibling ? "Another investigation is saving" : undefined}
        >
          {isBusy
            ? (editing ? "Updating…" : (isTranscript ? "Adding…" : "Saving…"))
            : blockedBySibling
            ? "Waiting…"
            : editing
            ? "Update"
            : isTranscript
            ? "Add"
            : "Save"}
        </button>
      </div>
        </div>
      </div>
    </div>
  );
};

LabInvestigationForm.propTypes = {
  /** Stable per-sheet id; namespaces DOM ids and keys the rail's bookkeeping. */
  formId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  patientId: PropTypes.number,
  /** null = blank add; an investigation record = editing that record. */
  editing: PropTypes.object,
  /** 1-based position in the stack, top first. */
  ordinal: PropTypes.number,
  showOrdinal: PropTypes.bool,
  /** A sibling sheet is mid-save; ours must wait so the version read is safe. */
  blockedBySibling: PropTypes.bool,
  onFilledChange: PropTypes.func,
  onSavingChange: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  /** Currently-selected appointment on the patient screen — stamped
   *  onto every Add / Update body. Missing / 0 → nothing sent. */
  appointmentId: PropTypes.number,
  // Transcript-mode extensions — all optional; unset preserves standalone.
  initialPayload: PropTypes.object,
  onSave: PropTypes.func,
  saving: PropTypes.bool,
  error: PropTypes.string,
  themeClass: PropTypes.string,
  /**
   * Lock the test picker as read-only. Add-mode transcript callers set
   * true when reopening an already-saved captured item (review click-
   * to-edit) so the reviewer can only tweak date / value / description
   * from there.
   */
  lockName: PropTypes.bool,
};

LabInvestigationForm.defaultProps = {
  initialPayload: null,
  onSave: null,
  saving: false,
  error: "",
  themeClass: "",
  lockName: false,
};

export default LabInvestigationForm;
